import { ItemTemplate, ModelTemplate, LocationReward } from './types';

interface RewardTemplates {
  modelTemplates?: ModelTemplate[];
  itemTemplates?: ItemTemplate[];
}

export interface LocationEffect {
  name: string;
  effect: string;
  choices?: {
    [key: string]: {
      description: string;
      effect?: string;
      requirements?: { faction?: string[] };
      action: (warband: any, templates?: RewardTemplates) => LocationReward;
    };
  };
  defaultAction?: (warband: any, templates?: RewardTemplates) => LocationReward;
}

function findItemTemplateId(name: string, templates?: ItemTemplate[]): string | undefined {
  if (!templates) return undefined;
  const normalized = name.toLowerCase();
  return templates.find((template) =>
    template.name.toLowerCase() === normalized || template.templateName.toLowerCase() === normalized
  )?.id;
}

function findModelTemplateId(name: string, templates?: ModelTemplate[]): string | undefined {
  if (!templates) return undefined;
  const normalized = name.toLowerCase();
  return templates.find((template) =>
    template.name.toLowerCase() === normalized || template.templateName.toLowerCase() === normalized
  )?.id;
}

export const LOCATION_EFFECTS: { [key: string]: LocationEffect } = {
  // COMMON EXPLORATION LOCATIONS
  "Moonshine Stash": {
    name: "Moonshine Stash",
    effect: "Choose one: Distribute, Destroy, or Sell",
    choices: {
      distribute: {
        description: "Distribute (New Antioch and Trench Pilgrims only): Add +2 DICE to Morale Checks in your next game",
        action: () => ({ type: 'special', message: "Add +2 DICE to Morale Checks in your next game manually." })
      },
      destroy: {
        description: "Destroy (New Antioch, Trench Pilgrims, and Iron Sultanate only): Up to two ELITE models earn +1 XP each",
        action: () => ({ type: 'special', message: "Choose up to two ELITE models and add +1 XP each manually. If this triggers advancement, make the roll immediately." })
      },
      sell: {
        description: "Sell (Any Warband): Add 30 👑 to your Strongbox",
        action: () => ({ type: 'ducats', ducats: 30, message: "Sold the moonshine for 30 ducats!" })
      }
    }
  },
  "Heavy Weapons Cache": {
    name: "Heavy Weapons Cache",
    effect: "Choose one: Surplus or Specialise",
    choices: {
      surplus: {
        description: "Surplus: Choose one piece of HEAVY Battlekit from your Armoury Tables and add it to your Arsenal",
        action: () => ({ type: 'special', message: "Choose one HEAVY Battlekit from your Armoury Tables and add it to your Arsenal manually." })
      },
      specialise: {
        description: "Specialise: Choose one Glory Item with a Cost of up to 5 ☼ and add it to your Arsenal",
        action: () => ({ type: 'special', message: "Choose one Glory Item costing up to 5 ☼ and add it to your Arsenal manually." })
      }
    }
  },
  "Trench Shrine": {
    name: "Trench Shrine",
    effect: "Choose one: Standard, Shrine, or Return",
    choices: {
      standard: {
        description: "Standard: Add a Troop Flag to your Warband's Arsenal",
        action: (_warband, templates) => {
          const itemId = findItemTemplateId("Troop Flag", templates?.itemTemplates);
          if (itemId) {
            return { type: 'item', itemTemplateIds: [itemId], message: "Added Troop Flag to your Arsenal." };
          }
          return { type: 'special', message: "Add a Troop Flag to your Arsenal manually." };
        }
      },
      shrine: {
        description: "Shrine: Add a Field Shrine to your Warband's Arsenal",
        action: (_warband, templates) => {
          const itemId = findItemTemplateId("Field Shrine", templates?.itemTemplates);
          if (itemId) {
            return { type: 'item', itemTemplateIds: [itemId], message: "Added Field Shrine to your Arsenal." };
          }
          return { type: 'special', message: "Add a Field Shrine to your Arsenal manually." };
        }
      },
      return: {
        description: "Return: Your Warband gains +2 ☼",
        action: () => ({ type: 'glory', glory: 2, message: "Gained 2 glory!" })
      }
    }
  },
  "Ruined House": {
    name: "Ruined House",
    effect: "Choose one: Rummage or Relic",
    choices: {
      rummage: {
        description: "Rummage: Choose one or more pieces of Equipment worth up to 30 👑 total",
        action: () => ({ type: 'special', message: "Choose Equipment worth up to 30 👑 total and add it to your Arsenal manually." })
      },
      relic: {
        description: "Relic: Choose one Glory Item worth up to 7 ☼",
        action: () => ({ type: 'special', message: "Choose one Glory Item worth up to 7 ☼ and add it to your Arsenal manually." })
      }
    }
  },
  "Survivor": {
    name: "Survivor",
    effect: "Add a faction-specific model to your Warband",
    defaultAction: () => ({ type: 'special', message: "Add the appropriate faction-specific Survivor model to your Warband manually." })
  },
  "Fallen Soldier": {
    name: "Fallen Soldier",
    effect: "Add Ranged Weapon, armor, and possibly additional equipment",
    defaultAction: () => ({ message: "Add one Ranged Weapon (up to 15 👑), Combat Helmet, Standard Armour, and roll D6 for possible additional equipment manually" })
  },
  "Trench Merchant": {
    name: "Trench Merchant",
    effect: "Choose one: Report or Trade",
    choices: {
      report: {
        description: "Report: Your Warband gains 2 ☼",
        action: () => ({ glory: 2, message: "Gained 2 glory!" })
      },
      trade: {
        description: "Trade: Unlock purchasing Glory Items costing 5 ☼ or less",
        action: () => ({ message: "You can now purchase Glory Items costing 5 ☼ or less in the Quartermaster Step manually" })
      }
    }
  },
  "Map & Document Bag": {
    name: "Map & Document Bag",
    effect: "Gain Reroll Exploration Skill",
    defaultAction: () => ({ type: 'skill', skills: ["Re-roll"], message: "Added Re-roll Exploration Skill to your Warband!" })
  },
  "Sniper's Lair": {
    name: "Sniper's Lair",
    effect: "Add faction-specific Battlekit to your Arsenal",
    defaultAction: () => ({ message: "Add the appropriate faction-specific Battlekit to your Arsenal manually" })
  },
  "Fallen Knight": {
    name: "Fallen Knight",
    effect: "Choose one: Loot or Memorialise",
    choices: {
      loot: {
        description: "Loot: Add Reinforced Armour, Trench Shield, Combat Helmet, and Sword or Polearm",
        action: () => ({ message: "Add Reinforced Armour, Trench Shield, Combat Helmet, and either a Sword or Polearm to your Arsenal manually" })
      },
      memorialise: {
        description: "Memorialise: Your Warband gains 2 ☼",
        action: () => ({ glory: 2, message: "Gained 2 glory in memorial!" })
      }
    }
  },
  "Warband Strongbox": {
    name: "Warband Strongbox",
    effect: "Choose one: Trove or Panoply",
    choices: {
      trove: {
        description: "Trove: Choose Battlekit worth up to 120 👑 total",
        action: () => ({ message: "Add Battlekit worth up to 120 👑 to your Arsenal manually" })
      },
      panoply: {
        description: "Panoply: Choose Glory Items worth up to 9 ☼ total (Weapons cost -1 ☼)",
        action: () => ({ message: "Add Glory Items worth up to 9 ☼ to your Arsenal (Weapons cost -1 ☼) manually" })
      }
    }
  },

  // RARE EXPLORATION LOCATIONS
  "Angelic Instrument": {
    name: "Angelic Instrument",
    effect: "Add Angelic Instrument to Arsenal",
    defaultAction: () => ({ message: "Add Angelic Instrument (Musical Instrument with 8\" range) to your Arsenal manually" })
  },
  "Abandoned Prophetic Radio Post": {
    name: "Abandoned Prophetic Radio Post",
    effect: "Gain Extra Dice Exploration Skill",
    defaultAction: () => ({ type: 'skill', skills: ["Extra dice"], message: "Added Extra dice Exploration Skill to your Warband!" })
  },
  "Pot of Manna": {
    name: "Pot of Manna",
    effect: "Add 10 👑 to loot each Exploration Step",
    defaultAction: () => ({ type: 'ducats', ducats: 10, message: "Add 10 👑 to this Exploration Step and note ongoing +10 👑 per Exploration Step manually" })
  },
  "Ransacked Alchemist Workshop": {
    name: "Ransacked Alchemist Workshop",
    effect: "Add Curative Fluids to Arsenal",
    defaultAction: () => ({ message: "Add Curative Fluids to your Arsenal manually" })
  },
  "Black Market": {
    name: "Black Market",
    effect: "Unlock purchasing Glory Items costing 8 ☼ or less",
    defaultAction: () => ({ message: "You can now purchase Glory Items costing 8 ☼ or less in the Quartermaster Step manually" })
  },
  "Book of Golems": {
    name: "Book of Golems",
    effect: "Add Takwin Homunculus Golem to your Warband",
    defaultAction: (_warband, templates) => {
      const modelTemplateId = findModelTemplateId("Takwin Homunculus", templates?.modelTemplates);
      if (modelTemplateId) {
        return {
          type: 'model',
          newModelTemplateId: modelTemplateId,
          message: "Added Takwin Homunculus to your Warband automatically."
        };
      }
      return {
        type: 'special',
        message: "Add Takwin Homunculus Golem with Human Hands and up to 50 👑 of Alchemical Formulas to your Warband manually"
      };
    }
  },
  "Ruined Church/Masjid/Synagogue": {
    name: "Ruined Church/Masjid/Synagogue",
    effect: "Choose one: Report or Keep",
    choices: {
      report: {
        description: "Report: Your Warband gains +4 ☼",
        action: () => ({ glory: 4, message: "Gained 4 glory!" })
      },
      keep: {
        description: "Keep: Choose one Glory Item worth up to 10 ☼",
        action: () => ({ message: "Add one Glory Item (up to 10 ☼) to your Arsenal manually" })
      }
    }
  },
  "Stash of Drugs & Erotica": {
    name: "Stash of Drugs & Erotica",
    effect: "Choose one: Indulge, Sell, or Confiscate",
    choices: {
      indulge: {
        description: "Indulge: Add +1 DICE to Morale Checks for rest of campaign",
        action: () => ({ type: 'special', message: "Add +1 DICE to Morale Checks for rest of campaign manually" })
      },
      sell: {
        description: "Sell: Your Warband gains 120 👑",
        action: () => ({ type: 'ducats', ducats: 120, message: "Sold the stash for 120 ducats!" })
      },
      confiscate: {
        description: "Confiscate: Your Warband gains +4 ☼ (Trench Pilgrims must choose this)",
        action: () => ({ type: 'glory', glory: 4, message: "Confiscated the stash for 4 glory!" })
      }
    }
  },
  "Saint's Reliquary": {
    name: "Saint's Reliquary",
    effect: "Add Saintly Relic to Arsenal",
    defaultAction: () => ({ message: "Add Saintly Relic to your Arsenal manually" })
  },
  "High-Ranking Captive": {
    name: "High-Ranking Captive",
    effect: "Choose one: Ransom, Execute, Imprison, or Indenture",
    choices: {
      ransom: {
        description: "Ransom: Your Warband gains 100 👑",
        action: () => ({ type: 'ducats', ducats: 100, message: "Ransomed the captive for 100 ducats!" })
      },
      execute: {
        description: "Execute: Your Warband gains +4 ☼",
        action: () => ({ type: 'glory', glory: 4, message: "Executed the captive for 4 glory!" })
      },
      imprison: {
        description: "Imprison: Choose one Glory Item with a Cost of up to 8 ☼",
        action: () => ({ type: 'special', message: "Add one Glory Item (up to 8 ☼) to your Arsenal manually" })
      },
      indenture: {
        description: "Indenture: Gain Set Dice Exploration Skill",
        action: () => ({ type: 'skill', skills: ["Set Dice"], message: "Added Set Dice Exploration Skill to your Warband!" })
      }
    }
  },
  "Abandoned Resurrection Machines": {
    name: "Abandoned Resurrection Machines",
    effect: "Add Salvaged Resurrection Machine to Arsenal",
    defaultAction: () => ({ message: "Add Salvaged Resurrection Machine to your Arsenal manually" })
  },

  // LEGENDARY EXPLORATION LOCATIONS
  "Battlefield of Corpses": {
    name: "Battlefield of Corpses",
    effect: "Choose up to 2 pieces of Battlekit worth up to 100 👑 total",
    defaultAction: () => ({ type: 'special', message: "Add up to 2 pieces of Battlekit worth up to 100 👑 total to your Arsenal manually" })
  },
  "Esoteric Library": {
    name: "Esoteric Library",
    effect: "Choose one: Burn, Release Plague, Sell, or Study",
    choices: {
      burn: {
        description: "Burn (New Antioch, Trench Pilgrims, Sultanate only): Your Warband gains 3+D3 ☼",
        action: () => ({ message: "Roll D3, add 3, and add that much glory manually" })
      },
      releasePlague: {
        description: "Release Plague (Black Grail only): Place INFECTION MARKER at start of each game",
        action: () => ({ message: "Note ongoing ability to place 1 INFECTION MARKER at start of each game manually" })
      },
      sell: {
        description: "Sell (Any Warband): Your Warband gains 6D6 x 10 👑",
        action: () => ({ message: "Roll 6D6, multiply by 10, and add that many ducats manually" })
      },
      study: {
        description: "Study (Court or Heretic Legions only): Place BLOOD MARKER at start of each game",
        action: () => ({ message: "Note ongoing ability to place 1 BLOOD MARKER at start of each game manually" })
      }
    }
  },
  "Hidden Passages": {
    name: "Hidden Passages",
    effect: "Gain Duplicate Exploration Skill",
    defaultAction: () => ({ type: 'skill', skills: ["Duplicate"], message: "Added Duplicate Exploration Skill to your Warband!" })
  },
  "Jabirean Alchemical Book": {
    name: "Jabirean Alchemical Book",
    effect: "Choose one: Keep, Sell, or Study",
    choices: {
      keep: {
        description: "Keep: Unlock purchasing Fire Shields",
        action: () => ({ message: "You can now purchase Fire Shields from House of Wisdom Armoury in the Quartermaster Step manually" })
      },
      sell: {
        description: "Sell: Your Warband gains 150 👑 or 5 ☼ (choose which)",
        action: () => ({ message: "Choose to add either 150 👑 or 5 ☼ manually" })
      },
      study: {
        description: "Study (House of Wisdom only): Alchemical Formulae cost -5 👑 (minimum 5 👑)",
        action: () => ({ message: "Note that Alchemical Formulae now cost -5 👑 (minimum 5 👑) manually" })
      }
    }
  },
  "Black Network Contact": {
    name: "Black Network Contact",
    effect: "Unlock purchasing Glory Items costing 12 ☼ or less",
    defaultAction: () => ({ message: "You can now purchase Glory Items costing 12 ☼ or less in the Quartermaster Step manually" })
  },
  "Treasure of the Holies": {
    name: "Treasure of the Holies",
    effect: "Score D3 Campaign Victory Points and add one Glory Item",
    defaultAction: () => ({ message: "Roll D3 for Campaign Victory Points and add one Glory Item of any cost to your Arsenal manually" })
  },
  "Skull of a Saint": {
    name: "Skull of a Saint",
    effect: "Add faction-specific Skull Relic",
    defaultAction: () => ({ message: "Add the appropriate faction-specific Skull Relic or Screaming Skull to your Arsenal manually" })
  },
  "Lock of Samson's Hair": {
    name: "Lock of Samson's Hair",
    effect: "Add Lock of Samson's Hair to Arsenal",
    defaultAction: () => ({ message: "Add Lock of Samson's Hair to your Arsenal manually" })
  },
  "Patron's Visit": {
    name: "Patron's Visit",
    effect: "Exchange up to 10 ☼ for Campaign Victory Points",
    defaultAction: () => ({ message: "Exchange up to 10 ☼ for equal Campaign Victory Points manually" })
  },
  "Sample of Holy DNA": {
    name: "Sample of Holy DNA",
    effect: "Treat one model with Holy DNA",
    defaultAction: () => ({ message: "Pick 1 model and note they have been treated with Holy DNA manually" })
  },
  "Golgotha Tektites": {
    name: "Golgotha Tektites",
    effect: "Treat up to two suits of Armour with Golgotha Tektites",
    defaultAction: () => ({ message: "Pick up to two suits of Armour and note they have been treated with Golgotha Tektites manually" })
  },
  "Fruit from the Tree of Good and Evil Knowledge": {
    name: "Fruit from the Tree of Good and Evil Knowledge",
    effect: "One model gains DEMONIC Keyword and one Skill of choice",
    defaultAction: () => ({ message: "Pick 1 model, add DEMONIC Keyword, and give them one Skill of your choice manually" })
  }
};
export function processLocationReward(locationName: string, choice?: string, warband?: any, templates?: RewardTemplates): LocationReward {
  const effect = LOCATION_EFFECTS[locationName];
  if (!effect) return { type: 'special', message: "Location effect not implemented yet" };

  let result: LocationReward = { type: 'special', message: "Effect applied!" };

  if (choice && effect.choices && effect.choices[choice]) {
    result = effect.choices[choice].action(warband, templates);
  } else if (effect.defaultAction) {
    result = effect.defaultAction(warband, templates);
  } else {
    return { type: 'special', message: "Location effect not implemented yet" };
  }

  // Ensure result has a message
  if (!result.message) {
    result.message = "Effect applied!";
  }

  if (!result.type) {
    result.type = 'special';
  }

  return result;
}
